import axios from 'axios';
import { useAuthStore, getAuthToken } from './store';

// CRITICAL: Always use window.location.origin to prevent deployment URL mismatches
// The REACT_APP_BACKEND_URL env var is IGNORED in production builds because it gets
// baked in at build time and causes issues when deploying to different domains.
const getApiUrl = () => {
  // In browser: ALWAYS use current origin - this is the only reliable method
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/api`;
  }
  // Fallback for SSR (won't happen in this app)
  return '/api';
};

const getBaseUrl = () => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return '';
};

// Computed at runtime from window.location
const API_URL = getApiUrl();
export const BASE_URL = getBaseUrl();

// DEBUG: Log the computed URLs
if (typeof window !== 'undefined') {
  console.log('[API DEBUG] window.location.origin:', window.location.origin);
  console.log('[API DEBUG] API_URL:', API_URL);
  console.log('[API DEBUG] BASE_URL:', BASE_URL);
}

// Helper to build full URL for assets (thumbnails, videos, etc.)
export const getAssetUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  if (path.startsWith('data:')) return path;  // Handle base64 data URLs
  return `${BASE_URL}${path}`;
};

// Helper to get the best available thumbnail URL (prefer cloud URL for persistence)
export const getThumbnailUrl = (video) => {
  if (!video) return '';
  // Prefer cloud thumbnail URL (persists after redeployment)
  if (video.cloud_thumbnail_url) return video.cloud_thumbnail_url;
  // Fallback to local thumbnail URL  
  if (video.thumbnail_url) return getAssetUrl(video.thumbnail_url);
  // No thumbnail available
  return '';
};

// Get video URL for playback (prefer cloud URL)
export const getVideoUrlForPlayback = (video) => {
  if (!video) return '';
  if (video.cloud_video_url) return video.cloud_video_url;
  if (video.video_url) {
    if (video.video_url.startsWith('http')) return video.video_url;
    return getAssetUrl(video.video_url);
  }
  return '';
};

// Helper to build full URL for video streaming with auth token
export const getVideoUrl = (path, token) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const baseUrl = BASE_URL || '';
  const timestamp = Date.now(); // Cache-busting timestamp
  const tokenParam = token ? `token=${encodeURIComponent(token)}` : '';
  const params = [tokenParam, `t=${timestamp}`].filter(Boolean).join('&');
  return `${baseUrl}${path}?${params}`;
};

// ============ AUTH API (No token, no interceptors) ============
// This is a completely separate instance for login/register
// It NEVER sends auth tokens and has no 401 handling
const authAxios = axios.create({
  baseURL: API_URL,
  headers: { 
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate, max-age=0',
    'Pragma': 'no-cache',
    'Expires': '0',
    'X-Requested-With': 'XMLHttpRequest'
  },
  timeout: 30000,
});

// DEBUG: Log axios baseURL
console.log('[API DEBUG] authAxios.defaults.baseURL:', authAxios.defaults.baseURL);

// Public API (alias for authAxios - no auth token)
const publicApi = authAxios;

// ============ MAIN API (With auth token) ============
const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

// Add auth token to authenticated requests only
// Also handle FormData - remove Content-Type so Axios sets it with proper boundary
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // CRITICAL: For FormData uploads, delete Content-Type so Axios auto-sets it with boundary
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

// Track when login happened to prevent immediate logout from race conditions
// Store in localStorage to persist across potential page reloads
export const setLoginTime = () => { 
  const time = Date.now();
  try {
    localStorage.setItem('login_timestamp', time.toString());
    console.log('[AUTH] Login time set:', time);
  } catch (e) {
    console.warn('[AUTH] Could not save login time:', e);
  }
};

const getLoginTime = () => {
  try {
    const stored = localStorage.getItem('login_timestamp');
    return stored ? parseInt(stored, 10) : 0;
  } catch (e) {
    return 0;
  }
};

// Handle 401 responses - DISABLED automatic logout to debug login issues
// The user will need to manually logout if their session expires
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.log('[AUTH] 401 received on:', error.config?.url);
      console.log('[AUTH] Automatic logout is DISABLED for debugging');
      // DO NOT logout or redirect - let the UI handle auth state
    }
    return Promise.reject(error);
  }
);

// ============ AUTH APIs (using authAxios - NO tokens) ============
export const authAPI = {
  register: (data) => authAxios.post('/auth/register', data),
  // Add timestamp AND random ID to prevent Chrome from caching login responses
  login: (data) => {
    const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    console.log('[AUTH_API] Login request ID:', requestId);
    return authAxios.post(`/auth/login?_t=${requestId}`, data);
  },
  getMe: () => api.get('/auth/me'),  // This one needs auth
  googleSession: (sessionId, referralCode = null) => {
    const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    console.log('[AUTH_API] Google session request ID:', requestId);
    const payload = { session_id: sessionId };
    if (referralCode) {
      payload.referral_code = referralCode;
    }
    return authAxios.post(`/auth/google/session?_t=${requestId}`, payload);
  },
  logout: () => api.post('/auth/logout', {}),
};

// ============ AUTHENTICATED APIs (using api - WITH tokens) ============

// Brand Guidelines APIs
export const settingsAPI = {
  getBrandGuidelines: () => api.get('/settings/brand-guidelines'),
  updateBrandGuidelines: (data) => api.put('/settings/brand-guidelines', data),
  getEngagementSettings: () => api.get('/settings/engagement'),
  updateEngagementSettings: (data) => api.put('/settings/engagement', data),
};

// Caption APIs
export const captionsAPI = {
  getStyles: () => api.get('/captions/styles'),
  applyToVideo: (videoId, style) => api.post(`/videos/${videoId}/add-captions`, { style }),
};

// Referral APIs
export const referralAPI = {
  getMyStats: () => api.get('/referral/my-stats'),
  getMyCode: () => api.get('/referral/my-code'),
  getHistory: (limit = 50, offset = 0) => api.get(`/referral/history?limit=${limit}&offset=${offset}`),
  getLeaderboard: (limit = 10) => api.get(`/referral/leaderboard?limit=${limit}`),
  updatePreferences: (rewardType) => api.put('/referral/preferences', { reward_type: rewardType }),
  requestPayout: (amount, method, paypalEmail = null) => api.post('/referral/request-payout', { amount, method, paypal_email: paypalEmail }),
  claimBonus: (bonusType = 'credits') => api.post(`/referral/claim-bonus?bonus_type=${bonusType}`),
  validateCode: (code) => publicApi.get(`/referral/validate/${code}`),
  getTiers: () => publicApi.get('/referral/tiers'),
};

// Notification APIs
export const notificationAPI = {
  getNotifications: (limit = 50, skip = 0, unreadOnly = false) => 
    api.get('/notifications', { params: { limit, skip, unread_only: unreadOnly } }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (notificationId) => api.post(`/notifications/${notificationId}/read`),
  markAllAsRead: () => api.post('/notifications/mark-all-read'),
  deleteNotification: (notificationId) => api.delete(`/notifications/${notificationId}`),
};

// Badge/Gamification APIs
export const badgeAPI = {
  getAllBadges: () => api.get('/badges'),
  getMyBadges: () => api.get('/badges/my-badges'),
  getProgress: () => api.get('/badges/progress'),
  claimReward: (badgeId) => api.post(`/badges/${badgeId}/claim`),
  getMilestones: () => api.get('/milestones'),
};

// Script Doctor APIs
export const scriptDoctorAPI = {
  analyze: (script, platform = 'tiktok', niche = 'general', tone = 'energetic', targetDuration = 30) =>
    api.post('/script-doctor/analyze', { script, platform, niche, tone, target_duration: targetDuration }),
  generateHooks: (topic, style = 'mixed', count = 5) =>
    api.post('/script-doctor/hooks', { topic, style, count }),
  improveCTA: (currentCta, productOrAction, urgencyLevel = 'medium') =>
    api.post('/script-doctor/improve-cta', { current_cta: currentCta, product_or_action: productOrAction, urgency_level: urgencyLevel }),
  addEmotionalTriggers: (script, emotions = null) =>
    api.post('/script-doctor/emotional-triggers', { script, emotions }),
  optimizePacing: (script, duration = 30) =>
    api.post('/script-doctor/optimize-pacing', { script, duration }),
  quickPolish: (script) =>
    api.post('/script-doctor/quick-polish', { script }),
};

// Viral Ghosting™ APIs
export const ghostAPI = {
  getTemplates: () => api.get('/ghost/templates'),
  getTemplate: (templateId) => api.get(`/ghost/templates/${templateId}`),
  analyzeVideo: (videoUrl) => api.post('/ghost/analyze', { video_url: videoUrl }),
  generatePrompt: (data) => api.post('/ghost/generate-prompt', data),
  searchBackgrounds: (query, category = null, orientation = 'portrait') =>
    api.get('/ghost/backgrounds', { params: { query, category, orientation } }),
  getPopularBackgrounds: (category = 'nature') =>
    api.get('/ghost/backgrounds/popular', { params: { category } }),
  validateOutput: (data) => api.post('/ghost/validate-output', data),
  getProfiles: (limit = 20) => api.get('/ghost/profiles', { params: { limit } }),
};

// Trends APIs
export const trendsAPI = {
  // Use public endpoint that doesn't require auth
  getTrends: (refresh = false) => api.get('/trends/public', { params: { refresh } }),
  getTrend: (id) => api.get(`/trends/${id}`),
  refreshTrends: () => api.post('/trends/refresh'),
};

// Generation APIs
export const generateAPI = {
  generateScript: (data) => api.post('/generate/script', data),
  generateCommentResponse: (data) => api.post('/generate/comment-response', data),
  generateHashtags: (data) => api.post('/generate/hashtags', data),
  generateTitle: async (description) => {
    const response = await api.post('/generate/title', { description });
    return response.data;
  },
};

// Videos APIs
export const videosAPI = {
  getVideos: (status, createdForClient = false) => api.get('/videos', { 
    params: { 
      status, 
      ...(createdForClient ? { created_for_client: true } : {}) 
    } 
  }),
  getVideo: (id) => api.get(`/videos/${id}`),
  createVideo: (data) => api.post('/videos', data, { timeout: 600000 }), // 10 min for video generation
  createEnhanced: (data) => api.post('/videos/enhanced', data, { timeout: 600000 }), // 10 min
  updateVideo: (id, data) => api.put(`/videos/${id}`, data),
  deleteVideo: (id) => api.delete(`/videos/${id}`),
  approveVideo: (id) => api.post(`/videos/${id}/approve`),
  rejectVideo: (id) => api.post(`/videos/${id}/reject`),
  retryVideo: (id) => api.post(`/videos/${id}/retry`, {}, { timeout: 600000 }),
  retryVideoNoVoice: (id) => api.post(`/videos/${id}/retry-no-voice`, {}, { timeout: 600000 }),
  getVideoStreamUrl: (id) => `${API_URL}/videos/${id}/stream`,
  getVideoDownloadUrl: (id) => `${API_URL}/videos/${id}/download`,
  getThumbnailUrl: (id) => `${API_URL}/videos/${id}/thumbnail`,
  getSubtitlesUrl: (id) => `${API_URL}/videos/${id}/subtitles`,
  getSubtitlesDownloadUrl: (id) => `${API_URL}/videos/${id}/subtitles/download`,
  getVideoProgress: (id) => api.get(`/videos/${id}/progress`),
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// UGC Ad Studio APIs
export const ugcAPI = {
  generateScript: async (data) => {
    // Use longer timeout for LLM calls
    const response = await api.post('/ugc/generate-script', data, { timeout: 60000 });
    return response.data;
  },
  generateHooks: async (data) => {
    const response = await api.post('/ugc/generate-hooks', data, { timeout: 60000 });
    return response.data;
  },
  generateHashtags: async (data) => {
    const response = await api.post('/ugc/generate-hashtags', data, { timeout: 60000 });
    return response.data;
  },
  predictViralScore: async (data) => {
    const response = await api.post('/ugc/predict-viral-score', data);
    return response.data;
  },
  generateAd: async (data, onProgress) => {
    // Handle file upload - supports multiple product images for element binding
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (key === 'presenterImage' && data[key]) {
        formData.append('presenter_image', data[key]);
      } else if (key === 'productImages' && Array.isArray(data[key])) {
        // Append multiple product images for element binding
        data[key].forEach((file, index) => {
          formData.append('product_images', file);
        });
      } else if (key === 'productImage' && data[key]) {
        // Legacy single image support
        formData.append('product_image', data[key]);
      } else if (key === 'logoImage') {
        if (data[key]) formData.append('logo_image', data[key]);
      } else if (key === 'secondItem') {
        if (data[key]) {
          formData.append('secondItemName', data[key].name || '');
          formData.append('secondItemType', data[key].type);
          data[key].images.slice(0, 3).forEach((f) => formData.append('second_product_images', f));
        }
      } else if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, typeof data[key] === 'object' ? JSON.stringify(data[key]) : data[key]);
      }
    });
    
    const response = await api.post('/ugc/generate-ad', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000, // 10 minutes for video generation
    });
    return response.data;
  },
  generateVariations: async (data) => {
    const response = await api.post('/ugc/generate-variations', data);
    return response.data;
  },
  checkScriptFit: async (data) => {
    const response = await api.post('/ugc/check-script-fit', data);
    return response.data;
  },
  fitScript: async (data) => {
    const response = await api.post('/ugc/fit-script', data, { timeout: 90000 });
    return response.data;
  },
  
  // A/B Testing APIs
  abTesting: {
    getPresets: async () => {
      const response = await api.get('/ugc/ab-testing/presets');
      return response.data;
    },
    createBatch: async (data) => {
      const response = await api.post('/ugc/ab-testing/create-batch', data);
      return response.data;
    },
    generateAll: async (batchId) => {
      const response = await api.post(`/ugc/ab-testing/generate-all/${batchId}`);
      return response.data;
    },
    getBatchStatus: async (batchId) => {
      const response = await api.get(`/ugc/ab-testing/batch/${batchId}`);
      return response.data;
    },
    listBatches: async (skip = 0, limit = 20) => {
      const response = await api.get('/ugc/ab-testing/batches', { params: { skip, limit } });
      return response.data;
    },
  },
  
  analyzeCompetitor: async (url) => {
    const formData = new FormData();
    formData.append('url', url);
    const response = await api.post('/ugc/analyze-competitor', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  
  scrapeProductUrl: async (url) => {
    const response = await api.post('/ugc/scrape-product', { url });
    return response.data;
  },
  
  analyzeProductImage: async (imageFile) => {
    const formData = new FormData();
    formData.append('image', imageFile);
    const response = await api.post('/ugc/analyze-product-image', formData);
    return response.data;
  },
  
  getQualityOptions: async () => {
    const response = await api.get('/ugc/quality-options');
    return response.data;
  },

  outfitPreview: async ({ presenterImage, productImages, scene, backgroundMode, tryonProductType, previewId, editNote, secondItem }) => {
    const formData = new FormData();
    formData.append('presenter_image', presenterImage);
    productImages.slice(0, 3).forEach((f) => formData.append('product_images', f));
    formData.append('scene', scene || '');
    formData.append('backgroundMode', backgroundMode || 'original');
    formData.append('tryonProductType', tryonProductType || 'clothing');
    formData.append('previewId', previewId || '');
    formData.append('editNote', editNote || '');
    if (secondItem) {
      secondItem.images.slice(0, 3).forEach((f) => formData.append('second_images', f));
      formData.append('secondItemType', secondItem.type);
      formData.append('secondItemName', secondItem.name || '');
    }
    const response = await api.post('/ugc/outfit-preview', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 });
    return response.data;
  },

  getMustShowIdeas: async ({ productName, productDescription, productImage }) => {
    const formData = new FormData();
    formData.append('productName', productName || '');
    formData.append('productDescription', productDescription || '');
    if (productImage) formData.append('product_image', productImage);
    const response = await api.post('/ugc/must-show-ideas', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    return response.data.ideas || [];
  },

  
  // Viral Templates - Multi-template support
  generateViralVideo: async (data) => {
    const formData = new FormData();
    
    // Face image is required
    if (data.faceImage) {
      formData.append('face_image', data.faceImage);
    }
    
    // Core parameters
    if (data.template) formData.append('template', data.template);
    if (data.musicMood) formData.append('music_mood', data.musicMood);
    if (data.duration) formData.append('duration', data.duration);
    
    // Template-specific parameters
    if (data.style) formData.append('style', data.style);
    if (data.action) formData.append('action', data.action);
    if (data.setting) formData.append('setting', data.setting);
    if (data.costume) formData.append('costume', data.costume);
    if (data.petType) formData.append('pet_type', data.petType);
    if (data.decade) formData.append('decade', data.decade);
    if (data.heroType) formData.append('hero_type', data.heroType);
    
    // Transformation template parameters
    if (data.transformFromDescription) formData.append('transform_from_description', data.transformFromDescription);
    if (data.transformToDescription) formData.append('transform_to_description', data.transformToDescription);
    if (data.transformAction) formData.append('transform_action', data.transformAction);
    if (data.transformDestination) formData.append('transform_destination', data.transformDestination);
    if (data.transformStyle) formData.append('transform_style', data.transformStyle);
    if (data.transformPreset) formData.append('transform_preset', data.transformPreset);
    
    // Singing Baby specific parameters
    if (data.audioSource) formData.append('audio_source', data.audioSource);
    if (data.presetSong) formData.append('preset_song', data.presetSong);
    if (data.videoUrl) formData.append('video_url', data.videoUrl);
    if (data.audioFile) formData.append('audio_file', data.audioFile);
    if (data.hasAudioFile) formData.append('has_audio_file', 'true');
    
    // Legacy support for danceStyle
    if (data.danceStyle) formData.append('dance_style', data.danceStyle);
    
    const response = await api.post('/viral/generate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000, // 10 minutes for video generation
    });
    return response.data;
  },
};

// Analytics APIs
export const analyticsAPI = {
  getViralScore: (data) => api.post('/analyze/viral-score', data),
  analyzeVideo: (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/analyze/video-viral', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180000, // 3 minutes for video analysis
      onUploadProgress: onProgress
    });
  },
  getHashtagAnalytics: () => api.get('/analytics/hashtags'),
};

// Templates APIs
export const templatesAPI = {
  getTemplates: () => api.get('/templates'),
  getTemplate: (id) => api.get(`/templates/${id}`),
};

// User Templates APIs (Script Template Gallery)
export const userTemplatesAPI = {
  getTemplates: (includeCommunity = true, category = null) => {
    const params = { include_community: includeCommunity };
    if (category) params.category = category;
    return api.get('/user-templates', { params });
  },
  getTemplate: (id) => api.get(`/user-templates/${id}`),
  createTemplate: (data) => api.post('/user-templates', data),
  updateTemplate: (id, data) => api.put(`/user-templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/user-templates/${id}`),
  useTemplate: (id) => api.post(`/user-templates/${id}/use`),
};

// Batch Export API
export const batchExportAPI = {
  exportVideos: (videoIds) => api.post('/videos/batch-export', { video_ids: videoIds }),
};

// Calendar APIs
export const calendarAPI = {
  getItems: () => api.get('/calendar'),
  createItem: (data) => api.post('/calendar', data),
  deleteItem: (id) => api.delete(`/calendar/${id}`),
};

// Sounds APIs
export const soundsAPI = {
  getTrendingSounds: (category) => api.get('/sounds/trending', { params: { category } }),
};

// Custom Music APIs
export const customMusicAPI = {
  uploadMusic: (formData) => api.post('/music/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getCustomMusic: () => api.get('/music/custom'),
  deleteMusic: (musicId) => api.delete(`/music/custom/${musicId}`),
  streamMusic: (musicId) => `${API_URL}/api/music/custom/${musicId}/stream`,
};

// Thumbnail APIs
export const thumbnailAPI = {
  generateThumbnail: (data) => api.post('/generate/thumbnail', data),
  getHistory: (limit = 20, skip = 0) => api.get('/thumbnails/history', { params: { limit, skip } }),
  getById: (id) => api.get(`/thumbnails/${id}`),
  delete: (id) => api.delete(`/thumbnails/${id}`),
};

// Image Studio APIs (AI Image Generation Suite)
export const imageStudioAPI = {
  // Generate new image from prompt
  generate: (data) => api.post('/image-studio/generate', data),
  // Edit existing image
  edit: (formData) => api.post('/image-studio/edit', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Generate variations of an image
  variations: (formData) => api.post('/image-studio/variations', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Resize image for different platforms
  resize: (formData) => api.post('/image-studio/resize', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Enhance image (upscale, denoise, etc.)
  enhance: (formData) => api.post('/image-studio/enhance', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Face tools (swap, expression, age, enhance)
  faceTools: (formData) => api.post('/image-studio/face-tools', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Generate thumbnail (legacy compatibility)
  generateThumbnail: (data) => api.post('/generate/thumbnail', data),
  // Get image history/gallery
  getHistory: (limit = 50, skip = 0, filter = 'all') => 
    api.get('/image-studio/history', { params: { limit, skip, filter } }),
  // Get single image
  getById: (id) => api.get(`/image-studio/${id}`),
  // Delete image
  delete: (id) => api.delete(`/image-studio/${id}`),
  // Remove background
  removeBackground: (formData) => api.post('/image-studio/remove-background', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// TikTok APIs
export const tiktokAPI = {
  connect: (username, accountType = 'creator', setAsDefault = false) => 
    api.post('/tiktok/connect', { username, account_type: accountType, set_as_default: setAsDefault }),
  getAccounts: () => api.get('/tiktok/accounts'),
  getAccount: () => api.get('/tiktok/account'),
  getAccountById: (accountId) => api.get(`/tiktok/accounts/${accountId}`),
  setDefaultAccount: (accountId) => api.put(`/tiktok/accounts/${accountId}/default`),
  disconnectAccount: (accountId) => api.delete(`/tiktok/accounts/${accountId}`),
  disconnect: () => api.delete('/tiktok/disconnect'),
  getAnalytics: (accountId = null) => api.get('/tiktok/analytics', { params: accountId ? { account_id: accountId } : {} }),
  getVideos: (limit = 20, sortBy = 'views', accountId = null) => 
    api.get('/tiktok/videos', { params: { limit, sort_by: sortBy, ...(accountId ? { account_id: accountId } : {}) } }),
  importToLibrary: (videoId) => api.post(`/tiktok/import-to-library/${videoId}`),
  // OAuth Integration
  getOAuthLoginUrl: () => api.get('/auth/tiktok/login'),
  getOAuthAccounts: () => api.get('/tiktok/oauth/accounts'),
  getOAuthStatus: () => api.get('/tiktok/oauth/status'),
  disconnectOAuth: (accountId) => api.delete(`/tiktok/oauth/disconnect/${accountId}`),
  // Publish to TikTok
  publish: (data) => api.post('/tiktok/publish', data),
  getPublishStatus: (videoId) => api.get(`/tiktok/publish/status/${videoId}`),
};

// Instagram APIs
export const instagramAPI = {
  connect: (username) => api.post('/instagram/connect', { username }),
  getAccount: () => api.get('/instagram/account'),
  disconnect: () => api.delete('/instagram/disconnect'),
  getAnalytics: () => api.get('/instagram/analytics'),
  prepareExport: (data) => api.post('/instagram/prepare-export', data),
  generateHashtags: (topic, niche = 'general', count = 15) => 
    api.post('/instagram/generate-hashtags', null, { params: { topic, niche, count } }),
};

// Cross-Post APIs
export const crossPostAPI = {
  create: (data) => api.post('/crosspost/create', data),
  list: (status) => api.get('/crosspost/list', { params: { status } }),
  delete: (postId) => api.delete(`/crosspost/${postId}`),
  postNow: (postId) => api.post(`/crosspost/${postId}/post-now`),
  adaptCaption: (caption, hashtags) => api.post('/ai/adapt-caption', { caption, hashtags }),
};

// Admin APIs
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getUgcQueueSettings: () => api.get('/admin/settings/ugc-queue'),
  getActionSuccessReport: (weeks = 4) => api.get('/admin/reports/action-success', { params: { weeks } }),
  getStorageStatus: () => api.get('/admin/storage-status'),
  updateUgcQueueSettings: (maxConcurrent) => api.put('/admin/settings/ugc-queue', { max_concurrent: maxConcurrent }),
  getUsers: (skip = 0, limit = 50, search = undefined) => 
    api.get('/admin/users', { params: { skip, limit, ...(search ? { search } : {}) } }),
  getUser: (userId) => api.get(`/admin/users/${userId}`),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  getVideos: (skip = 0, limit = 50, status = undefined, userId = undefined) =>
    api.get('/admin/videos', { params: { skip, limit, ...(status ? { status } : {}), ...(userId ? { user_id: userId } : {}) } }),
  getVideoDetail: (videoId) => api.get(`/admin/videos/${videoId}`),
  getUserVideos: (userEmail, skip = 0, limit = 50, status = undefined) =>
    api.get(`/admin/users/${encodeURIComponent(userEmail)}/videos`, { params: { skip, limit, ...(status ? { status } : {}) } }),
  deleteVideo: (videoId) => api.delete(`/admin/videos/${videoId}`),
  makeAdmin: (email) => api.post(`/admin/make-admin/${email}`),
  setupFirstAdmin: () => api.post('/admin/setup-first-admin'),
  
  // Enhanced User Profile APIs
  getUserUsageStats: (userId) => api.get(`/admin/users/${userId}/usage-stats`),
  getUserActivityLogs: (userId, limit = 50) => api.get(`/admin/users/${userId}/activity-logs`, { params: { limit } }),
  getUserPaymentHistory: (userId) => api.get(`/admin/users/${userId}/payment-history`),
  getUserNotes: (userId) => api.get(`/admin/users/${userId}/notes`),
  addUserNote: (userId, content) => api.post(`/admin/users/${userId}/notes`, { content }),
  deleteUserNote: (userId, noteId) => api.delete(`/admin/users/${userId}/notes/${noteId}`),
  grantCredits: (userId, amount, reason = null) => 
    api.post(`/admin/users/${userId}/grant-credits`, { amount, reason }),
  assignSubscription: (email, plan, duration, paymentRef = null) =>
    api.post('/admin/subscription/assign', { user_email: email, plan, duration, payment_reference: paymentRef }),
  sendPasswordReset: (userId) => api.post(`/admin/users/${userId}/send-password-reset`),
  
  // Referral admin endpoints
  getReferralStats: () => api.get('/admin/referrals/stats'),
  getPendingPayouts: () => api.get('/admin/referrals/pending-payouts'),
  processPayout: (payoutId, action, note = null) => 
    api.post(`/admin/referrals/process-payout/${payoutId}`, { action, admin_note: note }),
  adjustCommission: (referralId, adjustmentType, amount, reason) => 
    api.post('/admin/referrals/adjust-commission', { referral_id: referralId, adjustment_type: adjustmentType, amount, reason }),
  // Manual referral assignment
  assignReferral: (userEmail, referrerCode) =>
    api.post('/admin/referrals/assign', { user_email: userEmail, referrer_code: referrerCode }),
  bulkAssignReferrals: (assignments) =>
    api.post('/admin/referrals/bulk-assign', { assignments }),
  getUsersWithoutReferrer: (limit = 100, search = null) =>
    api.get('/admin/referrals/users-without-referrer', { params: { limit, ...(search ? { search } : {}) } }),
  // Referrer management
  getAllReferrers: (limit = 100, offset = 0, sortBy = 'referral_count', search = null) =>
    api.get('/admin/referrals/referrers', { params: { limit, offset, sort_by: sortBy, ...(search ? { search } : {}) } }),
  getReferrerDetails: (userId) =>
    api.get(`/admin/referrals/referrer/${userId}/details`),
  
  // Activity Logs
  getActivityLogs: (params = {}) => api.get('/admin/activity-logs', { params }),
  getActivityLogStats: () => api.get('/admin/activity-logs/stats'),
  resolveActivityLog: (logId) => api.put(`/admin/activity-logs/${logId}/resolve`),
  deleteActivityLog: (logId) => api.delete(`/admin/activity-logs/${logId}`),
  resolveAllActivityLogs: (logType = null) => 
    api.post('/admin/activity-logs/resolve-all', null, { params: logType ? { log_type: logType } : {} }),
  
  // Featured Videos (Admin Curated Demos)
  getFeaturedVideos: () => api.get('/admin/featured-videos'),
  toggleFeatureVideo: (videoId) => api.post(`/admin/videos/${videoId}/feature`),
  reorderFeaturedVideos: (videoOrders) => api.put('/admin/featured-videos/reorder', videoOrders),
  
  // Financials Dashboard
  getFinancialsOverview: () => api.get('/admin/financials/overview'),
  getRevenueChart: (period = '30d') => api.get('/admin/financials/revenue-chart', { params: { period } }),
  getCostBreakdown: () => api.get('/admin/financials/cost-breakdown'),
  getSubscriptionStats: () => api.get('/admin/financials/subscriptions'),
  
  // Account Manager Management
  getAccountManagers: () => api.get('/admin/account-managers'),
  getUnassignedClients: () => api.get('/admin/unassigned-clients'),
  assignClient: (clientId, managerId, notes = null) => 
    api.post('/admin/assign-client', { client_id: clientId, manager_id: managerId, notes }),
  transferClient: (clientId, fromManagerId, toManagerId, notes = null) =>
    api.post('/admin/transfer-client', { client_id: clientId, from_manager_id: fromManagerId, to_manager_id: toManagerId, notes }),
  removeClientAssignment: (clientId) => api.delete(`/admin/remove-client-assignment/${clientId}`),
  updateUserRole: (userId, role) => api.put(`/admin/users/${userId}/role`, null, { params: { role } }),
};

// Account Manager APIs
export const accountManagerAPI = {
  getMyClients: () => api.get('/account-manager/my-clients'),
  getClientDetails: (clientId) => api.get(`/account-manager/client/${clientId}`),
  getClientVideos: (clientId) => api.get(`/account-manager/client/${clientId}/videos`),
  getStats: () => api.get('/account-manager/stats'),
};

// Voice APIs (Preview + Cloning)
export const voiceAPI = {
  // Existing preview APIs
  getPreviewUrl: (voiceOption) => `${API_URL}/voice/preview/${voiceOption}`,
  getOptions: () => api.get('/voice/options'),
  
  // Voice Cloning APIs
  getMyVoices: () => api.get('/voices/my-voices'),
  getAllVoices: () => api.get('/voices/all'),
  // NOTE: Do NOT set Content-Type header manually for FormData - Axios auto-sets it with boundary
  cloneVoice: (formData) => api.post('/voices/clone', formData, {
    timeout: 120000,
  }),
  deleteVoice: (voiceId) => api.delete(`/voices/${voiceId}`),
  previewVoice: (formData) => api.post('/voices/preview', formData, {
    timeout: 60000,
  }),
  // African Languages (Khaya AI)
  getAfricanLanguages: () => api.get('/voices/african-languages'),
  // African Language Voice Samples (Preview)
  getAfricanVoiceSample: (voiceId) => api.get(`/voices/african-languages/sample/${voiceId}`, {
    timeout: 30000,
  }),
  // African Language Favorites
  getAfricanFavorites: () => api.get('/voices/african-languages/favorites'),
  addAfricanFavorite: (voiceId) => api.post(`/voices/african-languages/favorites/${voiceId}`),
  removeAfricanFavorite: (voiceId) => api.delete(`/voices/african-languages/favorites/${voiceId}`),
};

// fal.ai APIs
const FAL_VIDEO_TIMEOUT = 600000;

export const falAPI = {
  textToImage: (prompt, model = 'flux-schnell', imageSize = 'portrait_16_9', numImages = 1) => 
    api.post('/fal/text-to-image', { prompt, model, image_size: imageSize, num_images: numImages }, { timeout: 120000 }),
  imageToVideo: (imageUrl, prompt = '', model = 'kling-video', duration = '5') =>
    api.post('/fal/image-to-video', { image_url: imageUrl, prompt, model, duration }, { timeout: FAL_VIDEO_TIMEOUT }),
  textToVideo: (prompt, model = 'kling-text-video', duration = '5', aspectRatio = '9:16') =>
    api.post('/fal/text-to-video', { prompt, model, duration, aspect_ratio: aspectRatio }, { timeout: FAL_VIDEO_TIMEOUT }),
  tiktokVideo: (script, title, style = 'cinematic', duration = 15) =>
    api.post('/fal/tiktok-video', { script, title, style, duration }, { timeout: FAL_VIDEO_TIMEOUT }),
  getModels: () => api.get('/fal/models'),
};

// Stock Footage APIs (Pexels)
export const stockFootageAPI = {
  // Search stock footage
  search: (query, orientation = "portrait", footageType = "background") =>
    api.get('/stock-footage/search', { params: { query, orientation, footage_type: footageType } }),
  
  // Get categories
  getCategories: () => api.get('/stock-footage/categories'),
  
  // Get background B-roll footage
  getBackgrounds: (category = "lifestyle", orientation = "portrait", keywords = null) =>
    api.get('/stock-footage/backgrounds', { params: { category, orientation, keywords } }),
  
  // Get transition clips
  getTransitions: (style = "light_leak") =>
    api.get('/stock-footage/transitions', { params: { style } }),
  
  // Get product context footage
  getContext: (contextType = "lifestyle", productKeywords = null, orientation = "portrait") =>
    api.get('/stock-footage/context', { 
      params: { 
        context_type: contextType, 
        product_keywords: productKeywords,
        orientation 
      } 
    }),
  
  // Extract keywords from script
  extractKeywords: (script) => {
    const formData = new FormData();
    formData.append('script', script);
    return api.post('/stock-footage/extract-keywords', formData);
  },
  
  // Get available voice options for stock footage videos
  getVoices: () => api.get('/stock-footage/voices'),
  
  // Get voice preview audio URL
  getVoicePreviewUrl: (voiceId) => `${BASE_URL}/api/stock-footage/voice-preview/${voiceId}`,
  
  // Generate stock footage video with 3-tier voice options
  // Extended timeout: video generation can take 5-10 minutes
  generateVideo: (data) => api.post('/stock-footage/generate-video', data, { timeout: 600000 }),
};

// Music API for background music selection
export const musicAPI = {
  // Get available moods for filtering
  getMoods: () => api.get('/music/moods'),
  
  // Search music tracks
  search: (params) => api.get('/music/search', { params }),
  
  // Get AI-recommended tracks based on script
  getRecommendations: (script) => api.get('/music/recommend', { params: { script } }),
};

// Account Management API
export const accountAPI = {
  // Permanently delete user account and all associated data
  // Required for Google Play Store compliance
  deleteAccount: () => api.delete('/account/delete'),
};

// Credits API for pay-per-use credit system
export const creditsAPI = {
  // Get user's credit balance, bundles, and pricing
  getBalance: () => api.get('/credits'),
  
  // Check if user can afford an action
  checkAffordability: (action) => api.get('/credits/check', { params: { action } }),
  
  // Purchase credits (returns Mollie checkout URL)
  purchase: (bundleId) => api.post('/credits/purchase', { bundle_id: bundleId }),
  
  // Get transaction history
  getTransactions: (limit = 50, offset = 0, type = null) => 
    api.get('/credits/transactions', { params: { limit, offset, transaction_type: type } }),
  
  // Get credit bundles with pricing (public, no auth required)
  getBundles: (countryCode = null) => 
    api.get('/credits/bundles', { params: { country_code: countryCode } }),
};

export default api;
